/* eslint-disable camelcase */
import { Grid } from '@mui/material';
import { Coordinate, HexColor, Language } from '@nx-smartmonkey/shared/domain';
import {
  CrossIcon,
  GeoFenceDomainProps,
  GeoFencePaths,
  GoogleMapGeoFences,
  HSpacer,
  MarkerDomainTypes,
  RoutalDefaultColors,
  RoutalLoader,
  RoutalPalette,
} from '@nx-smartmonkey/ui';
import hash from 'object-hash';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import GeoFenceIcon from '../../../../../assets/img/geo_fence_icon.svg';
import { GeoFence } from '../../../../../domain/geoFence/GeoFence';
import { Project } from '../../../../../domain/project/Project';
import { Stop } from '../../../../../domain/stop/Stop';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useGetMapDefaults } from '../../../../../hooks/map/useGetMapDefaults';
import { useUpdateProjectGeoFence } from '../../../../../hooks/project/useUpdateProjectGeoFence';
import { useRetrieveRightPanelInfo } from '../../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useSetActiveLanguage } from '../../../../../hooks/translations/useSetActiveLanguage';
import { GeoFenceMapper } from '../../../../../mappers/geoFence/GeoFenceMapper';
import Button from '../../../../../react-components/Button';
import { PlaceSearch } from '../../../../../react-components/PlaceSearch';
import configuration from '../../../../../services/configuration';
import { PlannerGeoFencesActionsMenu } from '../../../../project-settings/planner-geo-fences/PlannerGeoFencesActionsMenu';
import { PlannerGeoFencesCreateDialog } from '../../../../project-settings/planner-geo-fences/PlannerGeoFencesCreateDialog';
import { useRetrieveActivePlanStops } from '../../hooks/plan-details/useRetrieveActivePlanStops';

const StyledCloseIcon = styled(CrossIcon)`
  width: 15px !important;
  height: 15px !important;
  font-size: 15px !important;
  color: #393939;
  cursor: pointer;
`;

const CloseIcon = ({ onClose }: { onClose?: (...args: any[]) => any }) => {
  return <StyledCloseIcon onClick={onClose} />;
};

const getDefaultGeoFence = (projectId: string) =>
  GeoFence.create({
    label: ``,
    enabled: true,
    project_id: projectId,
    color: HexColor.create(RoutalDefaultColors[0]),
    coordinates: [],
  });

type GeoFencesDialogMapContextAnchor = {
  screenPosition: { top: number; left: number };
  geoFence: GeoFence;
};

type GeoFencesDialogMapProps = {
  project: Project;
  geoFences: GeoFence[];
  highlightedGeoFenceId?: string;
  selectedGeoFenceIds: string[];
  editingGeoFence?: GeoFence;
  enableRightPanel?: boolean;
  setIsDrawingGeoFence: (isDrawing: boolean) => void;
  setEditingGeoFence: (geoFence?: GeoFence) => void;
  setSelectedGeoFenceIds: (selection: string[]) => void;
  setHighlightedGeoFenceId: (highlightedGeoFenceId: string | undefined) => void;
};

export const GeoFencesDialogMap = ({
  project,
  geoFences,
  highlightedGeoFenceId,
  selectedGeoFenceIds,
  editingGeoFence,
  enableRightPanel = true,
  setIsDrawingGeoFence,
  setEditingGeoFence,
  setSelectedGeoFenceIds,
  setHighlightedGeoFenceId,
}: GeoFencesDialogMapProps) => {
  const { data: activeLanguage } = useSetActiveLanguage();
  const { getZoom, getCoordinates } = useGetMapDefaults();
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();
  const { run: updateGeoFence, success: editSuccess } = useUpdateProjectGeoFence();
  const [isDrawingGeoFenceStep, setIsDrawingGeoFenceStep] = useState<`select_option` | `draw` | undefined>(undefined);
  const [newGeoFence, setNewGeoFence] = useState<GeoFence | undefined>(undefined);
  const [mapContextualAnchor, setMapContextualAnchor] = useState<GeoFencesDialogMapContextAnchor | undefined>(
    undefined
  );

  const { data: flatStops } = useRetrieveActivePlanStops();

  const geoFencesMemo = useMemo(() => {
    return geoFences
      .filter((geoFence) => geoFence.enabled)
      .map(
        (geoFence) =>
          ({
            id: geoFence.id,
            selected: geoFence.id === editingGeoFence?.id || selectedGeoFenceIds.includes(geoFence.id),
            hover: geoFence.id !== editingGeoFence?.id ? geoFence.id === highlightedGeoFenceId : false,
            editable: geoFence.id === editingGeoFence?.id,
            paths: geoFence.coordinatesToObject(),
            color: geoFence.color.value,
            eventsUpdateHash: hash({
              id: geoFence.id,
              editingGeoFence: editingGeoFence?.id,
              isDrawingGeoFenceStep,
              selectedGeoFenceIds,
            }),
            onClick: () => {
              if (isDrawingGeoFenceStep !== `draw` && !editingGeoFence) {
                if (selectedGeoFenceIds.includes(geoFence.id)) {
                  setSelectedGeoFenceIds(selectedGeoFenceIds.filter((selected) => selected !== geoFence.id));
                } else {
                  setSelectedGeoFenceIds([...selectedGeoFenceIds, geoFence.id]);
                }
              }
            },
            onDoubleClick: () => {
              if (isDrawingGeoFenceStep !== `draw` && !editingGeoFence && enableRightPanel) {
                setRightPanelInfo({
                  detailType: `geoFence`,
                  detailId: geoFence.id,
                  detailTab: null,
                });
              }
            },
            onMouseEnter: () => {
              if (isDrawingGeoFenceStep !== `draw` && !editingGeoFence) {
                setHighlightedGeoFenceId(geoFence.id);
              }
            },
            onMouseLeave: () => {
              if (isDrawingGeoFenceStep !== `draw` && !editingGeoFence) {
                setHighlightedGeoFenceId(undefined);
              }
            },
            onContextualMenu: (ev: any, geoFenceId: string) => {
              if (isDrawingGeoFenceStep !== `draw` && !editingGeoFence) {
                const geoFence = geoFences.find((geoFence) => geoFence.id === geoFenceId);
                if (geoFence) {
                  ev.preventDefault();
                  setMapContextualAnchor({
                    screenPosition: { top: ev.clientY, left: ev.clientX },
                    geoFence,
                  });
                }
              }
            },
            onPathChanged: (event: any, geoFenceId: string, newPaths: GeoFencePaths) => {
              if (editingGeoFence) {
                const newCoordinates: Coordinate[][] = [];
                newPaths.forEach((path) => {
                  const newPathCoords = path.map((coordinate) => {
                    const coord = Coordinate.create({ lat: coordinate.lat, lng: coordinate.lng });
                    return coord;
                  });

                  if (
                    newPathCoords.length > 0 &&
                    (newPathCoords[newPathCoords.length - 1].lat !== newPathCoords[0].lat ||
                      newPathCoords[newPathCoords.length - 1].lng !== newPathCoords[0].lng)
                  ) {
                    newPathCoords.push(newPathCoords[0]);
                  }

                  newCoordinates.push(newPathCoords);
                });
                const newGeoFence = GeoFenceMapper.cloneDomain(editingGeoFence!);
                newGeoFence.coordinates = newCoordinates;
                setEditingGeoFence(newGeoFence);
              }
            },
          } as GeoFenceDomainProps)
      );
  }, [
    geoFences,
    editingGeoFence,
    selectedGeoFenceIds,
    highlightedGeoFenceId,
    isDrawingGeoFenceStep,
    setSelectedGeoFenceIds,
    setRightPanelInfo,
    setHighlightedGeoFenceId,
    setEditingGeoFence,
    enableRightPanel,
  ]);

  useEffect(() => {
    if (editSuccess) {
      setEditingGeoFence(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSuccess]);

  const onSaveEditingGeoFence = () => {
    if (editingGeoFence) {
      updateGeoFence({ geoFence: editingGeoFence });
    }
  };

  const onCancelEditingGeoFence = () => {
    setEditingGeoFence(undefined);
  };

  const getCreateButtons = () => {
    if (!isDrawingGeoFenceStep) {
      return null;
    }
    switch (isDrawingGeoFenceStep) {
      case `select_option`:
        return (
          <Grid
            container
            direction="row"
            alignItems="center"
            style={{
              padding: `8px`,
              borderRadius: `4px`,
              backgroundColor: `${RoutalPalette.neutral00}bd`,
              border: `1px solid ${RoutalPalette.neutral10}`,
            }}
          >
            <Grid item style={{ width: `300px` }}>
              <PlaceSearch
                placeholder={intl.formatMessage({
                  id: `location`,
                })}
                // TODO: noOptionsMessage
                onUpdate={({
                  placeLabel,
                  placeCoordinates,
                }: {
                  placeLabel?: string;
                  placeCoordinates?: [number, number][][][];
                }) => {
                  if (placeLabel && placeCoordinates) {
                    const geoFenceToCreate = getDefaultGeoFence(project.id);
                    geoFenceToCreate.label = placeLabel;

                    geoFenceToCreate.coordinates = placeCoordinates.map((placeCoordinate) =>
                      placeCoordinate[0].map((coords) => Coordinate.fromArray([coords[1], coords[0]]))
                    );
                    setNewGeoFence(geoFenceToCreate);
                  }
                  setIsDrawingGeoFenceStep(undefined);
                }}
              />
            </Grid>

            <HSpacer small />

            <Button
              id="create-geo-fence-with-draw"
              styleType="secondary"
              style={{ margin: 0, padding: 0, width: `32px`, height: `32px` }}
              onClick={() => {
                setIsDrawingGeoFence(true);
                setIsDrawingGeoFenceStep(`draw`);
              }}
            >
              <img style={{ cursor: `pointer` }} width="20" src={GeoFenceIcon} alt="" draggable="false" />
            </Button>

            <HSpacer small />

            <CloseIcon onClose={() => setIsDrawingGeoFenceStep(undefined)} />
          </Grid>
        );
      case `draw`:
        if (!newGeoFence) {
          return (
            <Button
              styleType="secondary"
              onClick={() => {
                setIsDrawingGeoFence(false);
                setIsDrawingGeoFenceStep(undefined);
              }}
              intlId="cancel"
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  const getMarkers = useMemo(
    () => [
      ...(flatStops ?? [])
        .filter((flatStop: Stop) => {
          return flatStop.location && flatStop.location.isValid();
        })
        .map((stop: Stop) => ({
          id: stop.id,
          label: stop.label,
          lat: stop.location!.lat!,
          lng: stop.location!.lng!,
          kind: stop.type === `delivery` ? `default` : (`pickup` as MarkerDomainTypes),
        })),
    ],
    [flatStops]
  );

  return (
    <>
      <Grid container style={{ position: `relative` }}>
        {isDrawingGeoFenceStep !== undefined ? (
          <Grid
            item
            container
            wrap="nowrap"
            justifyContent="flex-end"
            alignItems="center"
            style={{
              position: `absolute`,
              top: `9px`,
              zIndex: 1,
              width: `auto`,
              right: `9px`,
            }}
          >
            <Grid item>{getCreateButtons()}</Grid>
          </Grid>
        ) : (
          <Grid
            item
            container
            wrap="nowrap"
            justifyContent="flex-end"
            alignItems="center"
            style={{
              position: `absolute`,
              top: `9px`,
              zIndex: 1,
              width: `auto`,
              right: `9px`,
            }}
          >
            <Grid item>
              {editingGeoFence ? (
                <Grid container direction="row">
                  <Button styleType="secondary" onClick={() => onCancelEditingGeoFence()} intlId="cancel" />
                  <HSpacer small />
                  <Button onClick={() => onSaveEditingGeoFence()} intlId="save" />
                </Grid>
              ) : (
                <>
                  {!newGeoFence ? (
                    <Button
                      styleType="secondary"
                      id="create-geo-fence"
                      onClick={() => setIsDrawingGeoFenceStep(`select_option`)}
                      intlId="planner.zones.create_geo_fence"
                    />
                  ) : null}
                </>
              )}
            </Grid>
          </Grid>
        )}

        <GoogleMapGeoFences
          height="100%"
          disableDoubleClickZoom
          language={activeLanguage?.locale ?? Language.getDefaultLanguage().value}
          apiKey={configuration.GOOGLE_MAPS_API_KEY}
          loadingComponent={<RoutalLoader width={60} />}
          center={geoFences.length === 0 ? getCoordinates() : undefined}
          zoomLevel={geoFences.length === 0 ? getZoom() : undefined}
          isDrawing={isDrawingGeoFenceStep === `draw`}
          onGeoFenceDrawn={(coordinates: { lat: number; lng: number }[]) => {
            const geoFenceToCreate = getDefaultGeoFence(project.id);
            geoFenceToCreate.coordinates = [coordinates.map((c) => Coordinate.create(c))];
            setNewGeoFence(geoFenceToCreate);
          }}
          geoFences={geoFencesMemo}
          markers={getMarkers}
        />

        {mapContextualAnchor ? (
          <PlannerGeoFencesActionsMenu
            type="contextual"
            contextAnchor={mapContextualAnchor}
            setContextAnchor={() => {
              setMapContextualAnchor(undefined);
            }}
            showViewDetails={false}
            showViewDetailsInGeoFences={true}
            showEdit={!editingGeoFence || editingGeoFence!.id !== mapContextualAnchor.geoFence.id}
            geoFences={[mapContextualAnchor.geoFence]}
            onEditGeoFence={(geoFence: GeoFence) => {
              setEditingGeoFence(geoFence);
            }}
          />
        ) : null}
      </Grid>

      {newGeoFence ? (
        <PlannerGeoFencesCreateDialog
          project={project}
          newGeoFence={newGeoFence}
          setNewGeoFence={setNewGeoFence}
          onClose={(created: boolean) => {
            setNewGeoFence(undefined);
            setIsDrawingGeoFenceStep(undefined);
            setIsDrawingGeoFence(false);
          }}
        />
      ) : null}
    </>
  );
};
